import { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { Alert, Box, Button, FormControlLabel, Switch, Typography } from "@mui/material";
import { pushPermissionState, pushPrePromptState, pushTokenState } from "@/lib/store";
import { usePushNativeReady } from "src/hooks/usePushNotifications";
import { getPushPreferences, PushPreferences, updatePushPreferences } from "@/controllers/push";

const DEFAULT_PREFS: PushPreferences = { newsEnabled: true, offersEnabled: true, alertsEnabled: true };

const ITEMS: { key: keyof PushPreferences; label: string; description: string }[] = [
    { key: "newsEnabled", label: "Weekly news", description: "The ORTEX weekly round-up of market-moving news." },
    { key: "offersEnabled", label: "Offers & promotions", description: "Occasional discounts and new features." },
    { key: "alertsEnabled", label: "Alerts", description: "The alerts you set with Push delivery." },
];

/**
 * "Phone notifications" in Settings (app only, ortex/ted/ted#2485). Preferences are
 * stored per device on the backend, keyed by this device's FCM token.
 */
const PhoneNotificationsSettings = () => {
    const token = useRecoilValue(pushTokenState);
    const permission = useRecoilValue(pushPermissionState);
    const setPrePrompt = useSetRecoilState(pushPrePromptState);
    const [prefs, setPrefs] = useState<PushPreferences>(DEFAULT_PREFS);
    const [saving, setSaving] = useState<keyof PushPreferences | null>(null);
    const [error, setError] = useState(false);
    const nativeReady = usePushNativeReady();

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        getPushPreferences(token).then((p) => {
            if (!cancelled && p) setPrefs(p);
        });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const toggle = async (key: keyof PushPreferences, value: boolean) => {
        if (!token) return;
        const previous = prefs;
        setPrefs({ ...prefs, [key]: value });
        setSaving(key);
        setError(false);
        const saved = await updatePushPreferences(token, { [key]: value });
        setSaving(null);
        if (saved) setPrefs(saved);
        else {
            setPrefs(previous);
            setError(true);
        }
    };

    const enabled = permission === "granted" && !!token;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
            {permission === "denied" && <Alert severity="info">Notifications are turned off for ORTEX on this phone. To get them, allow notifications for ORTEX in your phone&apos;s Settings.</Alert>}
            {permission === "prompt" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography variant="body2">Notifications are not turned on for this phone yet.</Typography>
                    {nativeReady ? (
                        <Button variant="contained" size="small" onClick={() => setPrePrompt({ open: true, reason: "launch" })}>
                            Turn on
                        </Button>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Update the ORTEX app from the store to turn them on.
                        </Typography>
                    )}
                </Box>
            )}
            {ITEMS.map((item) => (
                <Box key={item.key} sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <FormControlLabel sx={{ ml: 0 }} control={<Switch checked={prefs[item.key]} disabled={!enabled || saving === item.key} onChange={(e) => toggle(item.key, e.target.checked)} />} label={item.label} labelPlacement="start" />
                    <Typography variant="body2" color="text.secondary">
                        {item.description}
                    </Typography>
                </Box>
            ))}
            <Typography variant="body2" color="text.secondary">
                Ticker pulses are chosen per ticker in Market Intelligence, under Notifications.
            </Typography>
            {error && <Alert severity="error">Could not save that change. Please try again.</Alert>}
        </Box>
    );
};

export default PhoneNotificationsSettings;
